import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  SvgIcon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AppPrimaryButton,
  ConfirmDialog,
} from 'shared/components/AppComponents';
import { ReactComponent as DeleteIcon } from 'assets/images/trash_icon.svg';
import SimCardDownloadIcon from '@mui/icons-material/SimCardDownload';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { ReactElement, useState } from 'react';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { AIRiskBaselineService } from 'services/AIRiskBaselineService';
import { Toast, Utils } from 'shared/helpers';
import { useSelector } from 'react-redux';
import { IAppState } from 'state/RootReducer';

const PaginationComponent = ({ page, totalPages, onPageChange }: any): any => {
  if (totalPages <= 1) return null;
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 3,
        mt: 4,
      }}
    >
      <AppPrimaryButton
        className="btn-small"
        disabled={page === 1}
        onClick={() => onPageChange(page - 1)}
        title="Prev"
      />
      <Typography sx={{ fontSize: 14, color: '#555' }}>
        Page {page} of {totalPages}
      </Typography>
      <AppPrimaryButton
        className="btn-small"
        disabled={page === totalPages}
        onClick={() => onPageChange(page + 1)}
        title="Next"
      />
    </Box>
  );
};

const AssessmentHistory = ({
  completedAssessment,
  inProgressAssessment,
  completedPage,
  completedTotalPages,
  onCompletedPageChange,
  inProgressPage,
  inProgressTotalPages,
  onInProgressPageChange,
  handleGetInProgress,
}: any): ReactElement => {
  const navigate = useNavigate();
  const [openDeleteAssessment, setOpenDeleteAssessment] = useState<any>(null);
  const [isDownloadLoading, setIsDownloadLoading] = useState<any>(null);
  const { orgACLConfig } = useSelector(
    (state: IAppState) => state.baseAppState
  );

  const deleteAssessment = (id: any): void => {
    AIRiskBaselineService.deleteAssessment(id)
      .then(() => {
        Toast.showSuccessMessage('Assessment deleted successfully');
        handleGetInProgress();
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
      });
  };

  const downloadAssessment = (id: any): void => {
    setIsDownloadLoading(id);
    AIRiskBaselineService.downloadAssessment(id)
      .then((res: any) => {
        const data = res?.data;
        const blob =
          data instanceof Blob
            ? new Blob([data], { type: 'application/pdf' })
            : new Blob([data], { type: 'application/pdf' });
        const ID = id?.toString?.().slice(-6)?.toUpperCase() ?? ' ';

        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Assessment_${ID}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        setIsDownloadLoading(null);
      })
      .catch((error) => {
        setIsDownloadLoading(null);
        Utils.handleErrorResponse(error);
      });
  };

  const handleCloseDeleteAISystem = (): void => {
    setOpenDeleteAssessment(null);
  };

  return (
    <>
      <Card variant="outlined">
        <CardHeader
          title={
            <Typography variant="h5" fontWeight={700}>
              Assessment History
            </Typography>
          }
          subheader={
            <Typography fontSize={16} color="#7E7E7E" fontWeight={500} mt={3}>
              Current
            </Typography>
          }
        />
        <CardContent>
          {inProgressAssessment?.baselines.length > 0 ? (
            <>
              {inProgressAssessment.baselines.map(
                (data: any, i: any) =>
                  data.status === 'in_progress' && (
                    <Box
                      key={data?._id ?? i}
                      sx={{
                        borderRadius: 2,
                        padding: 4,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        flexWrap: 'wrap',
                        background: '#F1F5F4',
                        marginTop: i > 0 ? '0.5rem' : '',
                      }}
                    >
                      <Box
                        sx={{
                          flex: 1,
                          display: 'flex',
                          gap: '1rem',
                          alignItems: 'center',
                        }}
                      >
                        <Box>
                          <Typography fontWeight={700} fontSize={18}>
                            Started{' '}
                            {dayjs(data?.updatedAt ?? dayjs()).format(
                              'MMMM D, YYYY'
                            )}
                          </Typography>
                          <Typography fontSize={16} color="#7E7E7E">
                            {data?.mode
                              ? data.mode.charAt(0).toUpperCase() +
                                data.mode.slice(1)
                              : '-'}{' '}
                            · {data?.scoreSummary?.answeredPercent}% complete
                          </Typography>
                        </Box>
                        <Typography sx={{ color: '#e07b00', fontWeight: 600 }}>
                          In Progress
                        </Typography>
                      </Box>
                      {orgACLConfig.dashboard.includes(
                        'dashboard.aiRiskBaseLine.addComprehensive.continue'
                      ) && (
                        <AppPrimaryButton
                          className="btn-small"
                          onClick={() =>
                            navigate(`/ai-risk-baseline/survey/${data?._id}`)
                          }
                          sx={{
                            background: '#1a1a1a',
                            '&:hover': { background: '#333' },
                          }}
                          title="Continue"
                        />
                      )}
                      {orgACLConfig.dashboard.includes(
                        'dashboard.aiRiskBaseLine.addComprehensive.delete'
                      ) && (
                        <AppPrimaryButton
                          className="btn-danger btn-small"
                          onClick={() => setOpenDeleteAssessment(data?._id)}
                          startIcon={
                            <SvgIcon
                              component={DeleteIcon}
                              inheritViewBox
                              className="view-icon"
                              fontSize="inherit"
                            />
                          }
                          title="Delete"
                        />
                      )}
                    </Box>
                  )
              )}
              <PaginationComponent
                page={inProgressPage}
                totalPages={inProgressTotalPages}
                onPageChange={onInProgressPageChange}
              />
            </>
          ) : (
            <Box
              sx={{
                marginTop: '-1rem',
                color: '#777',
                fontWeight: 200,
                textAlign: 'center',
              }}
            >
              <Typography>No assessments in progress</Typography>
              <Typography>
                You have no ongoing assessments at the moment.
              </Typography>
            </Box>
          )}

          <Typography
            sx={{
              fontSize: '16px',
              color: '#7E7E7E',
              fontWeight: 500,
              margin: '16px 0',
            }}
          >
            Previous
          </Typography>
          {completedAssessment?.baselines.length > 0 ? (
            <>
              <TableContainer>
                <Table
                  size="small"
                  sx={{ borderCollapse: 'separate', borderSpacing: 0 }}
                >
                  <TableHead>
                    <TableRow>
                      {[
                        'Completed',
                        'Mode',
                        'Score',
                        'Maturity',
                        'Actions',
                      ].map((h) => (
                        <TableCell
                          key={h}
                          sx={{
                            height: '48px',
                            color: '#7E7E7E',
                            fontWeight: 500,
                            fontSize: 15,
                            border: '1px solid #E7E7E7',
                            borderBottom: '1px solid #E7E7E7',
                            '&:not(:first-of-type):not(:last-of-type)': {
                              borderLeft: 'none',
                              borderRight: 'none',
                            },
                            '&:first-of-type': {
                              borderRadius: '10px 0 0 0',
                              borderRight: 'none',
                            },
                            '&:last-of-type': {
                              borderRadius: '0 10px 0 0',
                              borderLeft: 'none',
                            },
                          }}
                        >
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {completedAssessment.baselines.map(
                      (data: any, i: any) =>
                        data.status === 'completed' && (
                          <TableRow
                            hover
                            key={i}
                            sx={{
                              '& td': {
                                background:
                                  i % 2 === 0 ? 'transparent' : '#f4f6fb',
                              },
                              height: '48px',
                            }}
                          >
                            <TableCell sx={{ fontWeight: 600, fontSize: 15 }}>
                              {dayjs(data?.updatedAt ?? dayjs()).format(
                                'DD/MM/YYYY'
                              )}
                            </TableCell>
                            <TableCell sx={{ color: '#777', fontSize: 15 }}>
                              {data?.mode
                                ? data.mode.charAt(0).toUpperCase() +
                                  data.mode.slice(1)
                                : '-'}
                            </TableCell>
                            <TableCell sx={{ fontSize: 15 }}>
                              {data?.scoreSummary?.scorePercent || '0'}%
                            </TableCell>
                            <TableCell sx={{ fontSize: 15 }}>
                              <Chip
                                sx={() => {
                                  const score =
                                    data?.scoreSummary?.scorePercent ?? 0;

                                  if (score < 20) {
                                    return {
                                      background: '#FEF2F2',
                                      color: '#DC2626',
                                      fontWeight: 600,
                                    };
                                  }
                                  if (score < 40) {
                                    return {
                                      background: '#FFF7ED',
                                      color: '#EA580C',
                                      fontWeight: 600,
                                    };
                                  }
                                  if (score < 60) {
                                    return {
                                      background: '#FEFCE8',
                                      color: '#CA8A04',
                                      fontWeight: 600,
                                    };
                                  }
                                  if (score < 80) {
                                    return {
                                      background: '#EFF6FF',
                                      color: '#2563EB',
                                      fontWeight: 600,
                                    };
                                  }
                                  return {
                                    background: '#F0FDF4',
                                    color: '#16A34A',
                                    fontWeight: 600,
                                  };
                                }}
                                label={data?.scoreSummary?.maturityLevel || '-'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Box
                                sx={{
                                  display: 'flex',
                                  gap: '1rem',
                                  alignItems: 'center',
                                }}
                              >
                                <Tooltip title="Download report">
                                  {isDownloadLoading === data?._id ? (
                                    <Box>
                                      <CircularProgress size={20} />
                                    </Box>
                                  ) : (
                                    <SimCardDownloadIcon
                                      sx={{ cursor: 'pointer' }}
                                      onClick={() =>
                                        downloadAssessment(data?._id)
                                      }
                                    />
                                  )}
                                </Tooltip>
                                {((data.mode === 'essential' &&
                                  orgACLConfig.dashboard.includes(
                                    'dashboard.aiRiskBaseLine.addEssential'
                                  )) ||
                                  (data.mode === 'comprehensive' &&
                                    orgACLConfig.dashboard.includes(
                                      'dashboard.aiRiskBaseLine.addComprehensive'
                                    ))) && (
                                  <Tooltip title="View questionnaire">
                                    <ArrowForwardIosIcon
                                      sx={{ cursor: 'pointer' }}
                                      onClick={() =>
                                        navigate(
                                          `/ai-risk-baseline/survey/${data?._id}`
                                        )
                                      }
                                    />
                                  </Tooltip>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        )
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <PaginationComponent
                page={completedPage}
                totalPages={completedTotalPages}
                onPageChange={onCompletedPageChange}
              />
            </>
          ) : (
            <Box
              sx={{
                marginTop: '-1rem',
                color: '#777',
                fontWeight: 200,
                textAlign: 'center',
              }}
            >
              <Typography>No completed assessments</Typography>
              <Typography>No assessments have been completed yet.</Typography>
            </Box>
          )}
        </CardContent>
      </Card>
      <ConfirmDialog
        title="Delete Assessment"
        subTitle="Are you sure to delete the assessment?"
        showConfirm={openDeleteAssessment !== null}
        cancelTitle="Close"
        successTitle="Delete"
        onSuccess={() => {
          handleCloseDeleteAISystem();
          deleteAssessment(openDeleteAssessment);
        }}
        onCancel={handleCloseDeleteAISystem}
        onCloseDialog={handleCloseDeleteAISystem}
      />
    </>
  );
};

export default AssessmentHistory;
